import { useState, useEffect } from 'react';
import { Plus, Film, Pencil, Trash2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useMediaDocumentaries,
  useCreateDocumentary,
  useUpdateDocumentary,
  useDeleteDocumentary,
  useUploadMediaImage,
  type MediaDocumentary,
} from '@/lib/api/hooks/use-media';
import { format } from 'date-fns';
import { motion } from 'framer-motion';

export function DocumentariesTab() {
  const [search, setSearch] = useState('');
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MediaDocumentary | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    thumbnailUrl: '',
    videoUrl: '',
    duration: '',
    youtubeEmbed: true,
    date: new Date().toISOString().split('T')[0],
  });

  const filters = {
    search: search || undefined,
  };

  const { data: items = [], isLoading } = useMediaDocumentaries(filters);
  const createItem = useCreateDocumentary();
  const updateItem = useUpdateDocumentary();
  const deleteItem = useDeleteDocumentary();
  const uploadImage = useUploadMediaImage();

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const result = await uploadImage.mutateAsync({ file, folder: 'media/documentaries' });
      setFormData((prev) => ({ ...prev, thumbnailUrl: result.url }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenDialog = (item?: MediaDocumentary) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        title: item.title,
        description: item.description,
        thumbnailUrl: item.thumbnailUrl || '',
        videoUrl: item.videoUrl,
        duration: item.duration || '',
        youtubeEmbed: item.youtubeEmbed,
        date: item.date ? new Date(item.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      });
    } else {
      setSelectedItem(null);
      setFormData({
        title: '',
        description: '',
        thumbnailUrl: '',
        videoUrl: '',
        duration: '',
        youtubeEmbed: true,
        date: new Date().toISOString().split('T')[0],
      });
    }
    setIsSheetOpen(true);
  };

  const extractYouTubeVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/]+)/,
      /youtube\.com\/shorts\/([^&\?\/]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  const extractYouTubeEmbedUrl = (url: string): string => {
    const videoId = extractYouTubeVideoId(url);
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return url;
  };

  const formatDuration = (isoDuration: string): string => {
    // Parse ISO 8601 duration format (PT1H2M10S) to readable format (1:02:10)
    const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return '';

    const hours = parseInt(match[1] || '0');
    const minutes = parseInt(match[2] || '0');
    const seconds = parseInt(match[3] || '0');

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const fetchYouTubeMetadata = async (url: string) => {
    const videoId = extractYouTubeVideoId(url);
    if (!videoId) return;

    setIsFetchingMetadata(true);
    try {
      // Use YouTube oEmbed API for title and thumbnail
      const oembedResponse = await fetch(
        `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
      );

      if (oembedResponse.ok) {
        const oembedData = await oembedResponse.json();

        setFormData(prev => {
          const updates: any = {};

          // Update title if empty
          if (!prev.title && oembedData.title) {
            updates.title = oembedData.title;
          }

          // Set thumbnail if available and not already set
          if (!prev.thumbnailUrl && oembedData.thumbnail_url) {
            updates.thumbnailUrl = oembedData.thumbnail_url;
          }

          return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
        });
      }
    } catch (error) {
    } finally {
      setIsFetchingMetadata(false);
    }
  };

  // Auto-fetch metadata when YouTube URL changes
  useEffect(() => {
    if (formData.youtubeEmbed && formData.videoUrl) {
      const videoId = extractYouTubeVideoId(formData.videoUrl);
      if (videoId) {
        const timeoutId = setTimeout(() => {
          fetchYouTubeMetadata(formData.videoUrl);
        }, 800); // Debounce to avoid too many requests
        return () => clearTimeout(timeoutId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.videoUrl, formData.youtubeEmbed]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Convert YouTube URL to embed format if needed
    const processedData = {
      ...formData,
      videoUrl: formData.youtubeEmbed
        ? extractYouTubeEmbedUrl(formData.videoUrl)
        : formData.videoUrl,
    };

    if (selectedItem) {
      await updateItem.mutateAsync({ id: selectedItem.id, data: processedData });
    } else {
      await createItem.mutateAsync(processedData);
    }

    setIsSheetOpen(false);
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    await deleteItem.mutateAsync(selectedItem.id);
    setIsDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  if (isLoading) {
    return <div className="text-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Filters and Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <Input
          placeholder="Search documentaries..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:max-w-xs"
        />
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Add Documentary
        </Button>
      </div>

      {/* Documentaries List */}
      {items.length === 0 ? (
        <EmptyState
          icon={Film}
          title="No documentaries yet"
          description="Start adding documentaries to showcase your work."
          action={{
            label: 'Add Documentary',
            onClick: () => handleOpenDialog(),
          }}
        />
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="flex flex-col md:flex-row gap-4 p-4">
                {/* Thumbnail/Video Preview */}
                <div className="md:w-1/3">
                  {item.youtubeEmbed && item.videoUrl ? (
                    <div className="aspect-video relative overflow-hidden rounded-md">
                      <iframe
                        src={item.videoUrl}
                        title={item.title}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  ) : item.thumbnailUrl ? (
                    <div className="aspect-video relative overflow-hidden rounded-md bg-muted">
                      <img
                        src={item.thumbnailUrl}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Play className="w-12 h-12 text-white" />
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-video relative overflow-hidden rounded-md bg-muted flex items-center justify-center">
                      <Film className="w-12 h-12 text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 flex flex-col">
                  <h3 className="font-semibold text-lg text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {item.description}
                  </p>
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mb-4">
                    {item.duration && (
                      <span className="flex items-center gap-1">
                        <Play className="w-3 h-3" />
                        {item.duration}
                      </span>
                    )}
                    <span>{format(new Date(item.date), 'MMM dd, yyyy')}</span>
                    <span className="capitalize">
                      {item.youtubeEmbed ? 'YouTube' : 'Direct Upload'}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDialog(item)}
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedItem(item);
                        setIsDeleteDialogOpen(true);
                      }}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>
              {selectedItem ? 'Edit Documentary' : 'Add Documentary'}
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground min-h-[120px]"
                required
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="youtubeEmbed"
                checked={formData.youtubeEmbed}
                onChange={(e) => setFormData({ ...formData, youtubeEmbed: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="youtubeEmbed" className="text-sm font-medium">
                YouTube Embed
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {formData.youtubeEmbed ? 'YouTube Video URL *' : 'Video URL *'}
              </label>
              <Input
                value={formData.videoUrl}
                onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                placeholder={formData.youtubeEmbed ? 'https://www.youtube.com/watch?v=VIDEO_ID or https://youtu.be/VIDEO_ID' : 'https://...'}
                required
              />
              {formData.youtubeEmbed && (
                <p className="text-xs text-muted-foreground mt-1">
                  Paste any YouTube link (watch, share, or embed URL). It will be automatically converted.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Thumbnail (Optional)</label>
              <div className="space-y-2">
                {formData.thumbnailUrl && (
                  <div className="aspect-video relative overflow-hidden rounded-md border border-border">
                    <img
                      src={formData.thumbnailUrl}
                      alt="Thumbnail preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  disabled={isUploading}
                />
                {isUploading && <p className="text-sm text-muted-foreground">Uploading...</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Duration (Optional)</label>
              <Input
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                placeholder="15:30 or 1:02:45"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter duration in format: minutes:seconds (e.g., 15:30) or hours:minutes:seconds (e.g., 1:02:45)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Date *</label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setIsSheetOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createItem.isPending || updateItem.isPending}>
                {selectedItem ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Documentary"
        description="Are you sure you want to delete this documentary? This action cannot be undone."
      />
    </div>
  );
}
